import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  MessageSquare,
  User,
  LayoutDashboard,
  Crown,
  Gift,
  Settings,
  UserCheck,
  Mail,
  Sparkles,
  Briefcase,
  Shield,
  IdCard,
  LogOut,
} from 'lucide-react';

/**
 * Authenticated user avatar dropdown — opens a rich menu of personal + admin shortcuts.
 */
export default function UserMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initial = user.display_name?.charAt(0) || user.username?.charAt(0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-10 w-10 rounded-full"
          data-testid="nav-user-menu"
        >
          <Avatar className="h-10 w-10 border-2 border-primary/20">
            <AvatarImage src={user.avatar_url} alt={user.display_name} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {initial}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end">
        <div className="flex items-center gap-3 p-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={user.avatar_url} />
            <AvatarFallback>{user.display_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{user.display_name}</span>
            <span className="text-xs text-muted-foreground">@{user.username}</span>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Community
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() =>
            navigate(`/beta-feedback?from=${encodeURIComponent(location.pathname)}`)
          }
          data-testid="menu-beta-feedback"
        >
          <MessageSquare className="mr-2 h-4 w-4" />
          Feedback & Updates
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate(`/profile/${user.username}`)}
          data-testid="menu-profile"
        >
          <User className="mr-2 h-4 w-4" />
          My Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
          <LayoutDashboard className="mr-2 h-4 w-4" />
          My Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/my-promo-page')}
          data-testid="menu-promo-page"
        >
          <Crown className="mr-2 h-4 w-4" />
          My Promo Page
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/business-card')}
          data-testid="menu-business-card"
        >
          <IdCard className="mr-2 h-4 w-4" />
          Business Card
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/handout-card')}
          data-testid="menu-handout-card"
        >
          <IdCard className="mr-2 h-4 w-4" />
          NGO Handout Card
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/referrals')} data-testid="menu-referrals">
          <Gift className="mr-2 h-4 w-4" />
          Referral Program
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
          <Settings className="mr-2 h-4 w-4" />
          Settings & Preferences
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/friends')} data-testid="menu-friends">
          <UserCheck className="mr-2 h-4 w-4" />
          Friends
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
          <Mail className="mr-2 h-4 w-4" />
          Messages
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/interests')} data-testid="menu-interests">
          <Sparkles className="mr-2 h-4 w-4" />
          Interests & Ads
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {user?.membership_tier === 'premium' && (
          <DropdownMenuItem
            onClick={() => navigate('/creator-dashboard')}
            data-testid="menu-creator-dashboard"
          >
            <Crown className="mr-2 h-4 w-4 text-amber-500" />
            Creator Dashboard
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate('/club-admin')} data-testid="menu-club-admin">
          <Settings className="mr-2 h-4 w-4" />
          Club Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
          <Settings className="mr-2 h-4 w-4" />
          Admin Settings
        </DropdownMenuItem>
        {(user?.is_platform_admin || user?.is_sysadmin) && (
          <DropdownMenuItem onClick={() => navigate('/sysadmin')} data-testid="menu-sysadmin">
            <Shield className="mr-2 h-4 w-4" />
            Sysadmin Dashboard
          </DropdownMenuItem>
        )}
        {(user?.is_admin || user?.is_operator_admin || user?.is_platform_admin || user?.is_sysadmin) && (
          <DropdownMenuItem onClick={() => navigate('/vbb/operations')} data-testid="menu-ops">
            <Briefcase className="mr-2 h-4 w-4" />
            Operations Center
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          className="text-destructive"
          data-testid="menu-logout"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
